import { useEffect, useState } from 'react';
import IdleTimer from 'react-idle-timer';
import formatDistance from 'date-fns/formatDistance';
import addMinutes from 'date-fns/addMinutes';
import isBefore from 'date-fns/isBefore';

import Grid from '@material-ui/core/Grid';
import Modal from 'components/CncUI/Modal';
import Button from 'components/CncUI/Button';

import useAuthentication from 'hooks/useAuthentication';
import Logger from 'utilities/logger';

const INTERVAL = 1000 * 60 * 28; // ms - request session status

const SessionManagement = () => {
  const { auth, logout } = useAuthentication();
  const [show, setShow] = useState(false);
  const [inTime, setInTime] = useState('');
  const [timeout, setTimeout] = useState(null);
  const logger = Logger('Session management');

  const checkSession = () => {
    auth
      .checkSession()
      .then(session => {
        if (!session) {
          logger.info('Session expired');
          logout().then(() => logger.info('Session expired'));
        }
      })
      .catch(err => logger.error(err));
  };

  const checkTimeout = time => {
    if (isBefore(time, new Date())) {
      logout().then(() => logger.info('Session closed'));
    } else {
      setInTime(formatDistance(time, new Date(), { addSuffix: true }));
    }
  };

  useEffect(() => {
    // Check if okta session is still valid every INTERVAL time
    const sessionTracker = setInterval(checkSession, INTERVAL);
    return () => clearInterval(sessionTracker);
  }, []);

  useEffect(() => {
    let timeTracker = null;
    if (show) {
      checkTimeout(timeout);
      timeTracker = setInterval(() => checkTimeout(timeout), 5000);
    }
    return () => {
      if (timeTracker) {
        clearInterval(timeTracker);
      }
    };
  }, [show]);

  const enableTimeout = () => {
    if (!timeout) {
      logger.debug('Started timeout');
      const time = addMinutes(new Date(), 5);
      setTimeout(time);
      setInTime(formatDistance(time, new Date(), { addSuffix: true }));
      setShow(true);
    }
  };

  const close = () => {
    setTimeout(null);
    setShow(false);
  };

  return (
    <>
      <IdleTimer
        element={document}
        onIdle={() => enableTimeout()}
        timeout={1000 * 60 * 25}
      />
      {show && (
        <Modal
          open
          onClose={close}
          id="SessionManagement-Modal"
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          disableBackdropClick={true}
          disableEscapeKeyDown={true}
        >
          <Modal.Title id="alert-dialog-title">
            Session Expiring Soon
          </Modal.Title>
          <Modal.Content>
            <Modal.ContentText id="alert-dialog-description">
              Your session is about to expire, and you will be logged out{' '}
              {inTime}. If you would like to extend your session please click
              'Extend Session' below.
            </Modal.ContentText>
          </Modal.Content>
          <Modal.Actions>
            <Grid container spacing={2} justify="flex-end">
              <Grid item>
                <Button
                  danger
                  onClick={() => {
                    logout().then(() => logger.info('User logged out'));
                  }}
                >
                  Logout
                </Button>
              </Grid>
              <Grid item>
                <Button onClick={close}>Extend Session</Button>
              </Grid>
            </Grid>
          </Modal.Actions>
        </Modal>
      )}
    </>
  );
};

export default SessionManagement;
